/**
 * Router bookkeeping the navigator adds to the params of every route.
 *
 * It is not part of the address anyone typed or linked, so it never belongs in a
 * destination — carried through a forward it reaches the next screen as parameters it has
 * no reason to expect, and on web it is visible in the address bar.
 */
const ROUTER_INTERNAL_PARAM_PREFIX = '__EXPO_ROUTER';

/**
 * The names React Navigation reserves for nesting one navigator inside another, which a
 * link into a nested route leaves on the params of the route that caught it.
 *
 * Serialized into a forward they reach the destination as ordinary query parameters, and
 * the link's own query goes missing: it travels inside `path` and in the nested `params`,
 * neither of which a flat read of the params returns. `path` still holds the address as it
 * was linked, so it is where the query is recovered from.
 */
const NAVIGATION_NESTING_MARKER_NAMES = new Set(['screen', 'initial', 'pop']);

const NAVIGATION_NESTING_COMPANION_NAMES = new Set(['params', 'state', 'path']);

/**
 * The parameter names a path already spells out in its own segments.
 *
 * A dynamic segment arrives in the params as well as in the path, so serializing the params
 * wholesale appends the id a second time as a query string, and the route that owns it sees
 * a stray parameter it never expects. Taken from the router's segments rather than from the
 * path, because only the segments say which parts were dynamic.
 */
export function dynamicSegmentParamNames(segments: readonly string[]): Set<string> {
  const names = new Set<string>();

  segments.forEach((segment) => {
    const dynamic = /^\[(?:\.\.\.)?(.+)\]$/.exec(segment);

    if (dynamic?.[1]) {
      names.add(dynamic[1]);
    }
  });

  return names;
}

/**
 * Serializes route params into a query string that can be appended to a destination.
 *
 * Everything the caller passes survives except the two kinds of parameter that would make
 * the destination wrong rather than merely longer: router bookkeeping, and the names a
 * dynamic segment already carries in the path.
 *
 * Returns an empty string when nothing survives, so a caller can test it directly rather
 * than deciding separately whether to write a `?`.
 */
export function buildRouteQueryString(
  params: Record<string, string | string[] | undefined>,
  segments: readonly string[] = []
): string {
  const routeParamNames = dynamicSegmentParamNames(segments);

  const searchParams = new URLSearchParams();

  const isNestingLink = Object.keys(params).some((key) => NAVIGATION_NESTING_MARKER_NAMES.has(key));

  const isNestingName = (key: string) =>
    NAVIGATION_NESTING_MARKER_NAMES.has(key) ||
    (isNestingLink && NAVIGATION_NESTING_COMPANION_NAMES.has(key));

  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null) {
      return;
    }

    if (
      key.startsWith(ROUTER_INTERNAL_PARAM_PREFIX) ||
      isNestingName(key) ||
      routeParamNames.has(key)
    ) {
      return;
    }

    if (Array.isArray(value)) {
      value.forEach((entry) => searchParams.append(key, entry));
    } else {
      searchParams.set(key, value);
    }
  });

  const linkedPath = isNestingLink ? params.path : undefined;

  const linkedQuery =
    typeof linkedPath === 'string' ? linkedPath.split('#')[0]?.split('?')[1] : undefined;

  new URLSearchParams(linkedQuery).forEach((value, key) => {
    if (!searchParams.has(key) && !routeParamNames.has(key)) {
      searchParams.append(key, value);
    }
  });

  return searchParams.toString();
}

/**
 * Joins a path and a query string, tolerating a path that already carries one.
 */
export function withRouteQuery(path: string, query: string): string {
  if (!query) {
    return path;
  }

  return `${path}${path.includes('?') ? '&' : '?'}${query}`;
}
